import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { useGetCashbackInfo } from '@/domain/transfer/useCases'
import { useGetFeaturesSettings } from '@/domain/settings'
import { useGetWalletDataByCryptoCode } from '@/hooks/useGetWalletDataByCryptoCode'
import { useTheme } from '@/hooks/useTheme'
import { SmallInfo } from '@/assets/icons/SmallInfo'
import { ArrowUpTipOpen } from '@/assets/icons/ArrowUpTipOpen'

// Simple skeleton component for loading state
const SkeletonBox: React.FC<{
  width: string
  height: number
  borderRadius: number
}> = ({ width, height, borderRadius }) => {
  const theme = useTheme()

  return (
    <BaseView
      style={{
        width,
        height: `${height}px`,
        backgroundColor: theme.colors.background.secondaryGray + '40',
        borderRadius: `${borderRadius}px`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <BaseView
        style={{
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)`,
          animation: 'skeleton-shimmer 1.5s infinite',
        }}
      />
      <style>{`
        @keyframes skeleton-shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }
      `}</style>
    </BaseView>
  )
}

export const ActivityCashbackBanner: React.FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()

  // Get cashback currency dynamically from feature settings (matching mobile)
  const { data: featureSettings } = useGetFeaturesSettings()
  const cashbackProgram = featureSettings?.cashback?.cashback_programs?.[0]
  const cashbackCurrencyCode =
    cashbackProgram?.cashback_currency_code || cashbackProgram?.code || 'BTC'
  const isCashbackEnabled = cashbackProgram?.is_enabled !== false

  // Round to 5 minutes like RN version
  const period = useMemo(() => {
    const now = new Date()
    const minutes = Math.floor(now.getMinutes() / 5) * 5
    const rounded = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      now.getHours(),
      minutes,
      0,
      0,
    )
    return Math.floor(rounded.getTime() / 1000)
  }, [])

  const { data: cashbackInfo, isLoading: isLoadingCashbackInfo } =
    useGetCashbackInfo({
      period,
      currency: cashbackCurrencyCode as 'BTC' | 'ETH' | 'USDT' | 'XAUT' | 'SOL' | 'KWDK' | 'AEDK' | 'SARK',
    })

  const btcWalletData = useGetWalletDataByCryptoCode({
    cryptoCode: cashbackInfo?.currency || 'BTC',
  })

  if (!isCashbackEnabled) return null

  if (isLoadingCashbackInfo) {
    return (
      <BaseView>
        <SkeletonBox width="100%" height={195} borderRadius={24} />
      </BaseView>
    )
  }

  if (!cashbackInfo || !btcWalletData) {
    return null
  }

  return (
    <BaseView
      p={16}
      gap={8}
      rounded={24}
      style={{
        background: `linear-gradient(90.55deg, ${
          Array.isArray(btcWalletData.avatarBGRefColor)
            ? btcWalletData.avatarBGRefColor.join(', ')
            : btcWalletData.avatarBGRefColor || '#FFA91F, #FF832B'
        })`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Header row with avatar and info icon */}
      <BaseView row align="center" justify="space-between" fullWidth>
        <BaseView row align="center" gap={8}>
          <BaseView
            style={{
              backgroundColor: theme.colors.background.semiTransparent,
              borderRadius: '12px',
              width: '24px',
              height: '24px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={btcWalletData.icon}
              alt={btcWalletData.name}
              style={{
                width: 'auto',
                height: '15px',
                objectFit: 'contain',
              }}
            />
          </BaseView>
          <BaseText fontSize={14} fontFamily="regular" color="label.default">
            {t('banners:activityCashback.title')}
          </BaseText>
        </BaseView>

        <BaseView
          onClick={() => navigate('/cashback-info')}
          style={{
            cursor: 'pointer',
          }}
        >
          <SmallInfo
            size={24}
            color={theme.colors.background.semiTransparent}
          />
        </BaseView>
      </BaseView>

      {/* Total cashback amount (all-time) */}
      <BaseText fontSize={44} fontFamily="semibold" color="label.default">
        {cashbackInfo.formattedTotalPaidCashbackFiat}
      </BaseText>

      {/* Weekly crypto amount and cashback percentage */}
      <BaseView row align="center" justify="space-between" fullWidth>
        <BaseText fontSize={14} fontFamily="regular" color="label.default">
          {cashbackInfo.formattedWeeklyCryptoAmount}
        </BaseText>

        <BaseView
          px={8}
          py={4}
          rounded={20}
          style={{
            backgroundColor: theme.colors.background.semiTransparent,
          }}
        >
          <BaseText fontSize={14} fontFamily="medium" color="label.default">
            {t('banners:activityCashback.description', {
              percentage: `${cashbackInfo.tier_rate || 0}%`,
            })}
          </BaseText>
        </BaseView>
      </BaseView>

      {/* Divider line */}
      <BaseView
        fullWidth
        style={{
          height: '1px',
          backgroundColor: theme.colors.background.semiTransparent,
        }}
      />

      {/* Earned this week row */}
      <BaseView row align="center" justify="space-between" fullWidth>
        <BaseView row align="center" gap={6}>
          <BaseView
            style={{
              backgroundColor: theme.colors.background.semiTransparent,
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ArrowUpTipOpen
              size={16}
              color={theme.colors.icon.activeInputIcon}
            />
          </BaseView>
          <BaseText fontSize={14} fontFamily="regular" color="label.default">
            {t('banners:activityCashback.earnedThisWeek', {
              formattedAmount: cashbackInfo.formattedWeeklyFiatAmount,
            })}
          </BaseText>
        </BaseView>
        <BaseText fontSize={14} fontFamily="regular" color="label.default">
          {cashbackInfo.formattedWeeklyCryptoAmount}
        </BaseText>
      </BaseView>
    </BaseView>
  )
}
